import $ from 'jquery';
import { refreshScroll } from '../core/ScrollService.js';
import Utils from '../utils/Utils.js';
import { bindPartnerContactModal, enrichCriteriaWithLabels, persistFiltersToStorage, appendFromAnnuaireParam } from '../features/partnerContactModal.js';
import { initPartnersMobileFilter } from '../features/mobileFilter.js';
import { initPartnersCarousels } from '../features/partnersCarousel.js';
import { initButtons } from '../features/buttons.js';

const FILTER_GROUPS = ['certification', 'expertise', 'modalite', 'region', 'taille', 'secteur'];

const normalizeRecherche = (value) => {
    const parts = Array.isArray(value)
        ? value
        : String(value || '').split(',');
    const terms = [];
    const seen = new Set();
    parts.forEach((part) => {
        const term = String(part || '').trim();
        if (!term) {
            return;
        }
        const key = term.toLowerCase();
        if (seen.has(key)) {
            return;
        }
        seen.add(key);
        terms.push(term);
    });
    return terms;
};

const addRechercheTerm = (list, value) => {
    const term = String(value || '').trim();
    if (!term) {
        return normalizeRecherche(list);
    }
    return normalizeRecherche([...(Array.isArray(list) ? list : []), term]);
};

/**
 * Page Partenaires unifiée — hub (home) ↔ listing via AJAX + pushState.
 */
export class PartnersPage {
    static matches() {
        return !!document.querySelector('[data-partners-root]')
            || document.body.classList.contains('page-template-tpl-annuaire-partenaires');
    }

    init() {
        this.root = document.querySelector('[data-partners-root]');
        if (!this.root) {
            return;
        }

        this.filters = this.readFiltersFromUrl();
        this.requestToken = 0;
        this.scopeCache = new Map();
        this.scopeCacheTimer = null;
        this.scopeRequest = null;
        this.refineRequest = null;
        this.isList = this.root.hasAttribute('data-partners-list');

        bindPartnerContactModal({
            getCriteria: () => enrichCriteriaWithLabels(this.filters, this.root),
        });

        this.bindAll();
        this.afterDomSwap();
        this.persistFilters();
        this.prefetchScopes();
    }

    bindAll() {
        this.bindHubSearch();
        this.bindListSearch();
        this.bindScopeLinks();
        this.bindFilters();
        this.bindPagination();
        this.bindPills();
        this.bindReset();
        this.bindFicheLinks();
        this.bindPopState();
    }

    afterDomSwap() {
        initButtons(this.root);
        if (this.isList) {
            this.syncCheckboxState();
            this.updateResetVisibility();
            initPartnersMobileFilter(this.root);
        } else {
            initPartnersCarousels(this.root);
        }
    }

    partnersUrl() {
        return this.root.getAttribute('data-partners-url')
            || this.root.getAttribute('data-annuaire-url')
            || '/partenaires/';
    }

    annuaireUrl() {
        return this.partnersUrl();
    }

    readFiltersFromUrl() {
        const url = new URL(window.location.href);
        const filters = {
            recherche: normalizeRecherche([
                ...url.searchParams.getAll('recherche[]'),
                ...url.searchParams.getAll('recherche'),
            ]),
            page: Math.max(1, parseInt(url.searchParams.get('page') || '1', 10) || 1),
            vue: url.searchParams.get('vue') === 'liste' ? 'liste' : '',
        };

        FILTER_GROUPS.forEach((group) => {
            const raw = [
                ...url.searchParams.getAll(`${group}[]`),
                ...url.searchParams.getAll(group),
            ];
            const values = [];
            raw.forEach((entry) => {
                String(entry).split(',').forEach((part) => {
                    const value = part.trim();
                    if (value && value !== 'all') {
                        values.push(value);
                    }
                });
            });
            filters[group] = [...new Set(values)];
        });

        return filters;
    }

    hasListingState(filters = this.filters) {
        if (filters.vue === 'liste') {
            return true;
        }
        if ((filters.recherche || []).length) {
            return true;
        }
        if (filters.page && filters.page > 1) {
            return true;
        }
        return FILTER_GROUPS.some((group) => (filters[group] || []).length > 0);
    }

    buildUrl(filters = this.filters) {
        const url = new URL(this.partnersUrl(), window.location.origin);
        url.searchParams.delete('page');
        url.searchParams.delete('recherche');
        url.searchParams.delete('recherche[]');
        url.searchParams.delete('vue');
        FILTER_GROUPS.forEach((group) => {
            url.searchParams.delete(group);
            url.searchParams.delete(`${group}[]`);
        });

        const searchTerms = normalizeRecherche(filters.recherche);
        if (searchTerms.length) {
            url.searchParams.set('recherche', searchTerms.join(','));
        }

        FILTER_GROUPS.forEach((group) => {
            const values = (filters[group] || []).filter(Boolean);
            if (values.length) {
                url.searchParams.set(group, values.join(','));
            }
        });

        if (filters.page && filters.page > 1) {
            url.searchParams.set('page', String(filters.page));
        }

        // Toujours garder vue=liste quand demandé (page 1 / clear / pagination ne doivent pas renvoyer au hub).
        if (filters.vue === 'liste') {
            url.searchParams.set('vue', 'liste');
        }

        return url.href;
    }

    persistFilters() {
        persistFiltersToStorage(this.filters, this.root);
    }

    pushUrl() {
        Utils.updateUrl(this.buildUrl());
        this.persistFilters();
    }

    /** Hub vide ou liste `vue=liste` sans filtres — seuls scopes mis en cache. */
    isCacheableScope(filters = this.filters) {
        if ((filters.recherche || []).length) {
            return false;
        }
        if (filters.page && filters.page > 1) {
            return false;
        }
        if (FILTER_GROUPS.some((group) => (filters[group] || []).length > 0)) {
            return false;
        }
        return true;
    }

    scopeCacheKey(filters = this.filters) {
        return this.hasListingState(filters) ? 'list' : 'hub';
    }

    emptyScopeFilters(mode) {
        const filters = {
            recherche: [],
            page: 1,
            vue: mode === 'list' ? 'liste' : '',
        };
        FILTER_GROUPS.forEach((group) => {
            filters[group] = [];
        });
        return filters;
    }

    scopeAjaxData(filters = this.filters) {
        const data = {
            action: 'partenaires_scope_ajax',
            recherche: normalizeRecherche(filters.recherche).join(','),
            page: filters.page || 1,
            vue: filters.vue || '',
        };
        FILTER_GROUPS.forEach((group) => {
            data[group] = filters[group] || [];
        });
        return data;
    }

    prefetchScopes() {
        const targets = [];
        if (this.isList) {
            targets.push(this.emptyScopeFilters('hub'));
            if (!this.isCacheableScope() || this.scopeCacheKey() !== 'list') {
                targets.push(this.emptyScopeFilters('list'));
            }
        } else {
            targets.push(this.emptyScopeFilters('list'));
        }

        const load = (filters, delay) => {
            setTimeout(() => {
                if (!this.isCacheableScope(filters)) {
                    return;
                }
                const key = this.scopeCacheKey(filters);
                if (this.scopeCache.has(key)) {
                    return;
                }
                $.ajax({
                    url: ajaxVars.url,
                    type: 'GET',
                    dataType: 'json',
                    data: this.scopeAjaxData(filters),
                }).done((response) => {
                    if (response && response.success && response.data) {
                        this.scopeCache.set(key, response.data);
                    }
                });
            }, delay);
        };

        const start = () => targets.forEach((filters, i) => load(filters, i * 200));
        // Prefetch tôt : la recherche hub s’appuie sur le shell liste en cache
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(start, { timeout: 800 });
        } else {
            setTimeout(start, 200);
        }
    }

    refineAjaxData() {
        const data = {
            action: 'partenaires_ajax',
            format: 'json',
            recherche: normalizeRecherche(this.filters.recherche).join(','),
            page: this.filters.page || 1,
            vue: this.filters.vue || '',
        };
        FILTER_GROUPS.forEach((group) => {
            data[group] = this.filters[group] || [];
        });
        return data;
    }

    setLoading(on, mode = 'scope') {
        const active = !!on;
        this.root.classList.toggle('is-loading', active && mode === 'scope');
        this.root.classList.toggle('is-refining', active && mode === 'refine');

        const el = this.root.querySelector('[data-partners-loading]');
        if (el) {
            const showOverlay = active && mode === 'scope';
            el.setAttribute('aria-hidden', showOverlay ? 'false' : 'true');
            el.setAttribute('aria-busy', active ? 'true' : 'false');
        }

        const results = this.root.querySelector('[data-partners-annuaire-results]');
        if (results) {
            results.classList.toggle('is-loading', active && mode === 'refine');
        }
    }

    scrollToTop() {
        const navRaw = getComputedStyle(document.documentElement)
            .getPropertyValue('--nav-megamenu-navbar-height')
            .trim();
        const navH = parseInt(navRaw, 10) || 100;
        const target = this.root.querySelector('[data-partners-chrome]') || this.root;
        const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - navH);
        window.scrollTo({ top, behavior: 'smooth' });
    }

    applyRootMode(mode) {
        const isList = mode === 'list';
        const modeChanged = this.isList !== isList;
        this.isList = isList;
        this.root.classList.toggle('partners--list', isList);
        this.root.classList.toggle('partnersAnnuaire', isList);
        this.root.classList.toggle('partners--hub', !isList);
        this.root.classList.toggle('partnersHub', !isList);

        if (isList) {
            this.root.setAttribute('data-partners-list', '');
            this.root.removeAttribute('data-partners-home');
        } else {
            this.root.setAttribute('data-partners-home', '');
            this.root.removeAttribute('data-partners-list');
        }

        return modeChanged;
    }

    applyScope(data) {
        if (!data) {
            return false;
        }

        let modeChanged = false;
        if (data.root_mode) {
            modeChanged = this.applyRootMode(data.root_mode);
        }

        const chrome = this.root.querySelector('[data-partners-chrome]');
        const main = this.root.querySelector('[data-partners-main]');

        if (typeof data.html_chrome !== 'undefined' && chrome) {
            chrome.innerHTML = data.html_chrome;
        }
        if (typeof data.html_main !== 'undefined' && main) {
            main.innerHTML = data.html_main;
        }

        if (data.filters) {
            this.filters = {
                recherche: normalizeRecherche(data.filters.recherche),
                page: Math.max(1, parseInt(data.filters.page || 1, 10) || 1),
                vue: data.filters.vue === 'liste' ? 'liste' : '',
            };
            FILTER_GROUPS.forEach((group) => {
                const values = data.filters[group];
                this.filters[group] = Array.isArray(values) ? values.filter(Boolean) : [];
            });
        }

        this.afterDomSwap();
        refreshScroll(this.root);
        return modeChanged;
    }

    applyRefine(data) {
        if (data.root_mode === 'home' || (data.html_chrome && data.html_main && !this.hasListingState())) {
            return this.applyScope(data);
        }

        if (data.html_chrome && data.html_main && data.root_mode === 'list' && !this.isList) {
            return this.applyScope(data);
        }

        const $root = $(this.root);

        if (data.html) {
            const $parsed = $(`<div>${data.html}</div>`);
            const $results = $parsed.find('[data-partners-annuaire-results]');
            const $pills = $parsed.find('[data-partners-pills]');
            const $pagination = $parsed.find('[data-partners-pagination]');
            const $count = $parsed.find('[data-partners-results-count]');

            if ($results.length) {
                $root.find('[data-partners-annuaire-results]').replaceWith($results);
            }
            if ($pills.length) {
                const $existing = $root.find('[data-partners-pills]');
                if ($existing.length) {
                    $existing.replaceWith($pills);
                } else {
                    $root.find('.partnersAnnuaire__resultsHeader').after($pills);
                }
            } else {
                $root.find('[data-partners-pills]').remove();
            }
            if ($pagination.length) {
                const $existingPagination = $root.find('[data-partners-pagination]');
                if ($existingPagination.length) {
                    $existingPagination.replaceWith($pagination);
                } else {
                    $root.find('[data-partners-annuaire-results]').after($pagination);
                }
            } else {
                $root.find('[data-partners-pagination]').remove();
            }
            if ($count.length) {
                $root.find('[data-partners-results-count]').replaceWith($count);
            }
        }

        if (data.counts) {
            this.applyFilterCounts(data.counts);
        }

        if (data.filters) {
            this.filters = {
                recherche: normalizeRecherche(data.filters.recherche),
                page: Math.max(1, parseInt(data.filters.page || 1, 10) || 1),
                vue: data.filters.vue === 'liste' ? 'liste' : '',
            };
            FILTER_GROUPS.forEach((group) => {
                const values = data.filters[group];
                this.filters[group] = Array.isArray(values) ? values.filter(Boolean) : [];
            });
        }

        this.syncCheckboxState();
        this.updateResetVisibility();
        initButtons(this.root.querySelector('[data-partners-annuaire-results]') || this.root);
        refreshScroll(this.root.querySelector('[data-partners-annuaire-results]') || this.root);
        return false;
    }

    applyScopeResponse(data, push = false) {
        const modeChanged = this.applyScope(data);
        if (modeChanged) {
            this.scrollToTop();
        }
        if (push && data.url) {
            Utils.updateUrl(data.url);
        } else if (push) {
            this.pushUrl();
        }
        this.persistFilters();
    }

    fetchScope(push = true) {
        const token = ++this.requestToken;

        if (this.scopeCacheTimer) {
            window.clearTimeout(this.scopeCacheTimer);
            this.scopeCacheTimer = null;
        }
        if (this.scopeRequest && this.scopeRequest.abort) {
            this.scopeRequest.abort();
            this.scopeRequest = null;
        }
        if (this.refineRequest && this.refineRequest.abort) {
            this.refineRequest.abort();
            this.refineRequest = null;
        }

        // Feedback immédiat (URL) pendant le fetch — comme hub ressources
        if (push) {
            Utils.updateUrl(this.buildUrl());
            this.persistFilters();
        }

        const cacheable = this.isCacheableScope();
        const cacheKey = cacheable ? this.scopeCacheKey() : null;
        const cached = cacheKey ? this.scopeCache.get(cacheKey) : null;

        if (cached) {
            this.setLoading(true, 'scope');
            this.scopeCacheTimer = window.setTimeout(() => {
                this.scopeCacheTimer = null;
                if (token !== this.requestToken) {
                    return;
                }
                this.applyScopeResponse(cached, false);
                this.setLoading(false);
            }, 80);
            return null;
        }

        this.setLoading(true, 'scope');

        this.scopeRequest = $.ajax({
            url: ajaxVars.url,
            type: 'GET',
            dataType: 'json',
            data: this.scopeAjaxData(),
        }).done((response) => {
            if (token !== this.requestToken || !response || !response.success) {
                return;
            }
            const data = response.data || {};
            if (cacheable && cacheKey) {
                this.scopeCache.set(cacheKey, data);
            }
            this.applyScopeResponse(data, false);
            if (push && data.url) {
                Utils.updateUrl(data.url);
            }
        }).always(() => {
            if (token === this.requestToken) {
                this.scopeRequest = null;
                this.setLoading(false);
            }
        });

        return this.scopeRequest;
    }

    fetchResults(push = true) {
        if (!this.hasListingState()) {
            // Rester en liste si on était déjà en listing (clear / page 1).
            this.filters.vue = 'liste';
            return this.fetchScope(push);
        }

        // Première entrée listing depuis hub → scope complet (chrome + toolbar).
        if (!this.isList) {
            return this.fetchScope(push);
        }

        const token = ++this.requestToken;
        if (this.refineRequest && this.refineRequest.abort) {
            this.refineRequest.abort();
        }
        this.setLoading(true, 'refine');

        this.refineRequest = $.ajax({
            url: ajaxVars.url,
            type: 'GET',
            dataType: 'json',
            data: this.refineAjaxData(),
        }).done((response) => {
            if (token !== this.requestToken || !response || !response.success) {
                return;
            }
            const data = response.data || {};

            if (data.root_mode === 'home' || (data.html_chrome && data.html_main && !data.html)) {
                this.applyScope(data);
                this.scrollToTop();
            } else {
                this.applyRefine(data);
            }

            if (push && data.url) {
                Utils.updateUrl(data.url);
            } else if (push) {
                this.pushUrl();
            }
            this.persistFilters();
        }).always(() => {
            if (token === this.requestToken) {
                this.refineRequest = null;
                this.setLoading(false);
            }
        });

        return this.refineRequest;
    }

    normalizeFilterCount(rawCount) {
        if (typeof rawCount === 'number') {
            return rawCount;
        }
        if (typeof rawCount === 'string') {
            return parseInt(rawCount, 10) || 0;
        }
        if (rawCount && typeof rawCount === 'object') {
            const nested = rawCount.count;
            if (typeof nested === 'number') {
                return nested;
            }
            if (typeof nested === 'string') {
                return parseInt(nested, 10) || 0;
            }
        }
        return 0;
    }

    applyFilterCounts(counts) {
        FILTER_GROUPS.forEach((group) => {
            const groupCounts = counts[group] || {};
            Object.keys(groupCounts).forEach((slug) => {
                const count = this.normalizeFilterCount(groupCounts[slug]);
                const selector = `[data-filter-group="${group}"][data-filter-value="${slug}"]`;

                this.root.querySelectorAll(`[data-partners-filter]${selector}`).forEach((input) => {
                    const isChecked = input.checked;
                    const hide = count === 0 && !isChecked;
                    input.disabled = false;
                    const li = input.closest('.partnersAnnuaireFilterOption');
                    if (li) {
                        li.hidden = hide;
                        li.classList.remove('partnersAnnuaireFilterOption--disabled');
                    }
                });

                this.root.querySelectorAll(`[data-partners-filter-count]${selector}`).forEach((countEl) => {
                    countEl.textContent = String(count);
                });
            });
        });
    }

    syncCheckboxState() {
        FILTER_GROUPS.forEach((group) => {
            const active = this.filters[group] || [];
            this.root.querySelectorAll(`[data-partners-filter][data-filter-group="${group}"]`).forEach((input) => {
                input.checked = active.includes(input.getAttribute('data-filter-value'));
            });
        });

        const $search = $(this.root).find(
            '[data-partners-annuaire-search] input[type="search"], [data-partners-hub-search] input[type="search"]'
        );
        if ($search.length) {
            // Barre toujours vide : termes actifs = pills (accumulation OU)
            $search.val('');
        }

        // Sync hidden recherche for no-JS fallback forms
        const $form = $(this.root).find('[data-partners-annuaire-search]');
        let $hiddenRecherche = $form.find('input[name="recherche"]');
        const terms = normalizeRecherche(this.filters.recherche);
        if (terms.length) {
            if (!$hiddenRecherche.length && $form.length) {
                $hiddenRecherche = $('<input>', {
                    type: 'hidden',
                    name: 'recherche',
                }).prependTo($form);
            }
            $hiddenRecherche.val(terms.join(','));
        } else {
            $hiddenRecherche.remove();
        }
    }

    updateResetVisibility() {
        const resetBtn = this.root.querySelector('[data-partners-filters-reset]');
        if (!resetBtn) {
            return;
        }
        if (this.hasListingState() && (
            (this.filters.recherche || []).length
            || FILTER_GROUPS.some((group) => (this.filters[group] || []).length)
        )) {
            resetBtn.removeAttribute('hidden');
        } else {
            resetBtn.setAttribute('hidden', 'true');
        }
    }

    clearFiltersToList() {
        this.filters = {
            recherche: [],
            page: 1,
            vue: 'liste',
        };
        FILTER_GROUPS.forEach((group) => {
            this.filters[group] = [];
        });
        this.syncCheckboxState();
        this.pushUrl();
        this.fetchResults();
    }

    removeFilter(group, value) {
        if (group === 'recherche') {
            const needle = String(value || '').toLowerCase();
            this.filters.recherche = normalizeRecherche(this.filters.recherche)
                .filter((term) => term.toLowerCase() !== needle);
            $(this.root).find('[data-partners-annuaire-search] input[type="search"]').val('');
        } else if (Array.isArray(this.filters[group])) {
            this.filters[group] = this.filters[group].filter((item) => item !== value);
        }
        this.filters.page = 1;

        // Sans filtre/recherche : rester sur la liste (vue=liste), pas le hub.
        if (!this.hasListingState()) {
            this.filters.vue = 'liste';
        }

        this.syncCheckboxState();
        this.pushUrl();
        this.fetchResults();
    }

    bindHubSearch() {
        // Capture native : bloque le GET avant tout autre handler
        this.root.addEventListener('submit', (e) => {
            const form = e.target?.closest?.('[data-partners-hub-search]');
            if (!form || !this.root.contains(form)) {
                return;
            }
            e.preventDefault();
            e.stopImmediatePropagation();
            this.handleHubSearch(form);
        }, true);
    }

    handleHubSearch(form) {
        const $form = $(form);
        const value = ($form.find('input[type="search"]').val() || '').trim();
        this.filters.page = 1;
        FILTER_GROUPS.forEach((group) => {
            this.filters[group] = this.filters[group] || [];
        });
        $form.find('input[type="search"]').val('');

        if (!value) {
            this.filters.recherche = [];
            this.filters.vue = 'liste';
            this.fetchScope(true);
            return;
        }

        this.filters.recherche = addRechercheTerm([], value);
        this.filters.vue = '';

        // Hub → liste : si shell liste préfetché, swap instantané puis refine (pas 2s de scope).
        const cachedList = this.scopeCache.get('list');
        if (cachedList && !this.isList) {
            const savedFilters = {
                recherche: [...this.filters.recherche],
                page: this.filters.page,
                vue: this.filters.vue,
            };
            FILTER_GROUPS.forEach((group) => {
                savedFilters[group] = [...(this.filters[group] || [])];
            });

            Utils.updateUrl(this.buildUrl());
            this.persistFilters();
            this.applyScope({
                ...cachedList,
                root_mode: 'list',
                filters: savedFilters,
            });
            this.scrollToTop();
            this.fetchResults(true);
            return;
        }

        this.fetchScope(true);
    }

    bindListSearch() {
        this.root.addEventListener('submit', (e) => {
            const form = e.target?.closest?.('[data-partners-annuaire-search]');
            if (!form || !this.root.contains(form)) {
                return;
            }
            e.preventDefault();
            e.stopImmediatePropagation();
            this.handleListSearch(form);
        }, true);
    }

    handleListSearch(form) {
        const $form = $(form);
        const value = ($form.find('input[type="search"]').val() || '').trim();
        if (value) {
            this.filters.recherche = addRechercheTerm(this.filters.recherche, value);
            this.filters.vue = '';
        }
        this.filters.page = 1;
        if (
            !(this.filters.recherche || []).length
            && !FILTER_GROUPS.some((group) => (this.filters[group] || []).length)
        ) {
            this.filters.vue = 'liste';
        }
        $form.find('input[type="search"]').val('');
        this.pushUrl();
        this.fetchResults();
    }

    bindScopeLinks() {
        $(this.root).on('click', '[data-partners-scope-link]', (e) => {
            const link = e.currentTarget;
            const href = link.getAttribute('href');
            if (!href || link.getAttribute('target') === '_blank') {
                return;
            }

            let url;
            try {
                url = new URL(href, window.location.origin);
            } catch (err) {
                return;
            }

            if (url.origin !== window.location.origin) {
                return;
            }

            e.preventDefault();
            Utils.updateUrl(url.href);
            this.filters = this.readFiltersFromUrl();
            // Ne pas forcer vue=liste : le breadcrumb hub doit rester hub ;
            // les CTA liste portent déjà ?vue=liste (ou des filtres) dans le href.
            this.fetchScope(false);
        });
    }

    bindFilters() {
        $(this.root).on('change', '[data-partners-filter]', (e) => {
            const input = e.currentTarget;
            const group = input.getAttribute('data-filter-group');
            const value = input.getAttribute('data-filter-value');
            if (!group || !value) {
                return;
            }

            if (!Array.isArray(this.filters[group])) {
                this.filters[group] = [];
            }

            if (input.checked) {
                if (!this.filters[group].includes(value)) {
                    this.filters[group].push(value);
                }
            } else {
                this.filters[group] = this.filters[group].filter((item) => item !== value);
            }

            this.filters.page = 1;
            // Sans filtre/recherche actifs : rester en liste (pas hub).
            if (
                !(this.filters.recherche || []).length
                && !FILTER_GROUPS.some((g) => (this.filters[g] || []).length)
            ) {
                this.filters.vue = 'liste';
            }

            this.pushUrl();
            this.fetchResults();
        });
    }

    bindPagination() {
        $(this.root).on('click', '[data-partners-pagination] a', (e) => {
            e.preventDefault();
            const href = e.currentTarget.getAttribute('href');
            if (!href) {
                return;
            }
            const url = new URL(href, window.location.origin);
            Utils.updateUrl(href);
            this.filters = this.readFiltersFromUrl();
            // Re-read page from clicked URL specifically
            this.filters.page = Math.max(1, parseInt(url.searchParams.get('page') || '1', 10) || 1);
            // Pagination page 1 sans autres filtres : rester en liste.
            if (!this.hasListingState()) {
                this.filters.vue = 'liste';
            }
            this.fetchResults(false);
        });
    }

    bindPills() {
        $(this.root).on('click', '[data-partners-pill-remove]', (e) => {
            e.preventDefault();
            const pill = e.currentTarget.closest('[data-partners-pill]');
            if (!pill) {
                return;
            }
            this.removeFilter(
                pill.getAttribute('data-filter-group'),
                pill.getAttribute('data-filter-value')
            );
        });
    }

    bindReset() {
        $(this.root).on('click', '[data-partners-filters-reset]', (e) => {
            e.preventDefault();
            this.clearFiltersToList();
        });
    }

    bindFicheLinks() {
        $(this.root).on('click', '[data-partners-fiche-link]', (e) => {
            this.persistFilters();
            const link = e.currentTarget;
            const href = link.getAttribute('href');
            if (!href) {
                return;
            }
            const fromUrl = this.buildUrl({ ...this.filters, page: 1 });
            link.setAttribute('href', appendFromAnnuaireParam(href, fromUrl));
        });
    }

    bindPopState() {
        window.addEventListener('popstate', () => {
            this.filters = this.readFiltersFromUrl();
            this.persistFilters();
            // Back entre états liste → refine ; sinon scope (hub ↔ liste)
            if (this.hasListingState() && this.isList) {
                this.fetchResults(false);
            } else {
                this.fetchScope(false);
            }
        });
    }
}

